"use client";

import { externalLinkProps } from "@/lib/external-link-props";
import {
  useActionState,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Link from "next/link";
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock,
  Home,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Send,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  GoogleMapEmbed,
  mapEmbedUrlForAddress,
} from "@/components/google-map-embed";
import {
  buildContactLeadCopy,
  type ContactInquiryType,
  type ContactQuickLink,
} from "@/lib/vertical-lead-intents";
import {
  analytics,
  getFormControlFieldKey,
  getFormControlValidityErrorType,
  getLeadAttribution,
} from "@/lib/analytics";
import {
  useMarketingLocationContext,
  type MarketingRestaurantCopy,
} from "@/lib/marketing-location-context";
import {
  buildHomepageCopy,
  type RomaLocationCopy,
} from "@/lib/marketing-location-copy";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import {
  romaBranchLocations,
  type RestaurantSiteConfig,
} from "@/lib/restaurant-platform";
import { submitContactForm } from "./actions";

type ContactFormState = { success: boolean; error?: string } | null;

const saffronInputClass =
  "h-[50px] w-full rounded-[10px] border border-[#6d6d6d] bg-white px-4 text-[16px] leading-none text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#6f6f6f] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60";

const saffronTextareaClass =
  "min-h-[132px] w-full resize-none rounded-[10px] border border-[#6d6d6d] bg-white px-4 py-3 text-[16px] leading-6 text-[#1f1f1f] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#6f6f6f] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 disabled:cursor-not-allowed disabled:opacity-60";

const fieldGroups: Record<string, string> = {
  name: "contact",
  email: "contact",
  phone: "contact",
  inquiryType: "inquiry",
  inquiry_type: "inquiry",
  message: "details",
};

const fieldKeys: Record<string, string> = {
  inquiryType: "inquiry_type",
};

function buildMapUrl(siteConfig: RestaurantSiteConfig) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state} ${siteConfig.address.zip}`,
  )}`;
}

function formatTime(value: string) {
  const [hourPart = "0", minutePart = "0"] = value.split(":");
  const hour = Number(hourPart);
  const minute = Number(minutePart);
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return minute === 0 ? `${displayHour} ${suffix}` : `${displayHour}:${String(minute).padStart(2, "0")} ${suffix}`;
}

function formatDayLabel(days: string[]) {
  if (days.length === 7) return "Daily";
  if (days.length > 1) {
    return `${days[0].slice(0, 3)}-${days[days.length - 1].slice(0, 3)}`;
  }

  return days[0]?.slice(0, 3) ?? "Hours";
}

function formatWeeklyHoursFromEntries(
  weekly: { days: string[]; opens: string; closes: string }[],
) {
  return weekly.map(
    (hours) =>
      `${formatDayLabel(hours.days)} ${formatTime(hours.opens)}-${formatTime(hours.closes)}`,
  );
}

function formatWeeklyHours(siteConfig: RestaurantSiteConfig) {
  return formatWeeklyHoursFromEntries(siteConfig.hours.weekly);
}

function weeklyHoursForRomaLocation(
  location: RomaLocationCopy,
  siteConfig: RestaurantSiteConfig,
) {
  if (location.slug === "southern-highlands") {
    return formatWeeklyHours(siteConfig);
  }

  const branch = romaBranchLocations.find(
    (candidate) =>
      candidate.phone.replace(/\D/g, "") === location.phoneHref.replace(/\D/g, ""),
  );

  if (branch?.hours.weekly?.length) {
    return formatWeeklyHoursFromEntries(branch.hours.weekly);
  }

  return [location.hoursDetail];
}

function mapEmbedUrlForRomaLocation(location: RomaLocationCopy) {
  return `https://maps.google.com/maps?q=${encodeURIComponent(
    `${location.addressLine1}, ${location.addressLine2}`,
  )}&output=embed`;
}

function stickyCallLabel(location: RomaLocationCopy) {
  return location.slug === "southern-highlands" ? "SH" : location.eyebrow;
}

function isInquiryType(
  inquiryTypes: readonly { value: ContactInquiryType }[],
  value: FormDataEntryValue | null,
): value is ContactInquiryType {
  return (
    typeof value === "string" &&
    inquiryTypes.some((type) => type.value === value)
  );
}

export default function ContactPage() {
  const { location, siteConfig, restaurantCopy, romaFunnelEnabled } =
    useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(siteConfig);
  const isRomaDarkFunnel = pageBrand === "chefs-roma" && romaFunnelEnabled;
  const romaHomepageCopy =
    pageBrand === "chefs-roma" ? buildHomepageCopy(location) : null;
  const romaLocations = romaHomepageCopy?.romaLocations ?? [];
  const isDualRomaContact = romaLocations.length > 1;
  const phoneHref = restaurantCopy.phoneHref;
  const phoneDisplay = restaurantCopy.phoneDisplay;
  const mapUrl = buildMapUrl(siteConfig);
  const contactLeadCopy = buildContactLeadCopy({
    siteConfig,
    shortName: restaurantCopy.shortName,
    phoneDisplay,
    email: siteConfig.contact.email,
  });
  const contactRoutingLinks = contactLeadCopy.routingLinks;
  const contactSeoTrustPoints = contactLeadCopy.trustPoints;
  // Roma homepage FAQs already list both NAPs and phones — reuse them here.
  const contactSeoFaqs = isDualRomaContact
    ? (romaHomepageCopy?.faqItems ?? contactLeadCopy.faqs)
    : contactLeadCopy.faqs;
  const inquiryTypes = contactLeadCopy.inquiryTypes;
  const weeklyHours = formatWeeklyHours(siteConfig);
  const mapEmbedSrc = mapEmbedUrlForAddress(siteConfig);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [inquiryType, setInquiryType] = useState<ContactInquiryType | "">("");
  const hasStartedContactFormRef = useRef(false);
  const lastAttemptedAtRef = useRef(0);

  function markContactFormStarted(type = inquiryType || "unknown") {
    if (hasStartedContactFormRef.current) return;

    hasStartedContactFormRef.current = true;
    analytics.formStarted({
      category: "form",
      form_type: "contact",
      source: "contact_form",
      inquiry_type: type,
    });
  }

  function getContactFunnelProperties(
    properties: Record<string, string | number | boolean | undefined> = {},
  ) {
    return {
      form_type: "contact",
      source: "contact_form",
      inquiry_type: inquiryType || "unknown",
      ...properties,
    };
  }

  function trackContactAttempted(type = inquiryType || "unknown") {
    const now = Date.now();
    if (now - lastAttemptedAtRef.current < 750) return;

    lastAttemptedAtRef.current = now;
    analytics.formAttempted(
      getContactFunnelProperties({
        step: "details",
        inquiry_type: type || "unknown",
      }),
    );
  }

  function trackContactValidationFailure(
    fieldKey: string,
    errorType: string,
    type = inquiryType || "unknown",
  ) {
    const normalizedFieldKey = fieldKeys[fieldKey] ?? fieldKey;

    analytics.formValidationFailed(
      getContactFunnelProperties({
        step: "details",
        inquiry_type: type || "unknown",
        field_group:
          fieldGroups[fieldKey] ?? fieldGroups[normalizedFieldKey] ?? "details",
        field_key: normalizedFieldKey,
        error_type: errorType,
      }),
    );
  }

  const [formState, formAction, isPending] = useActionState(
    async (_prevState: ContactFormState, formData: FormData) => {
      const name = String(formData.get("name") || "").trim();
      const email = String(formData.get("email") || "").trim();
      const phone = String(formData.get("phone") || "").trim();
      const smsConsent = formData.get("smsConsent") === "on";
      const message = String(formData.get("message") || "").trim();
      const honeypot = String(formData.get("website") || "").trim();
      const inquiryTypeValue = formData.get("inquiryType");
      const submittedInquiryType = isInquiryType(inquiryTypes, inquiryTypeValue)
        ? inquiryTypeValue
        : inquiryType;
      const submittedInquiryTypeForAnalytics = submittedInquiryType || "unknown";

      markContactFormStarted(submittedInquiryTypeForAnalytics);
      trackContactAttempted(submittedInquiryTypeForAnalytics);

      if (!name || name.length < 2) {
        trackContactValidationFailure(
          "name",
          name ? "too_short" : "missing_required",
          submittedInquiryTypeForAnalytics,
        );
        toast.error("Please enter your name");
        return { success: false, error: "Name required" };
      }
      if (!email) {
        trackContactValidationFailure(
          "email",
          "missing_required",
          submittedInquiryTypeForAnalytics,
        );
        toast.error("Please enter your email");
        return { success: false, error: "Email required" };
      }
      if (!submittedInquiryType) {
        trackContactValidationFailure(
          "inquiryType",
          "missing_required",
          submittedInquiryTypeForAnalytics,
        );
        toast.error("Please select an inquiry type");
        return { success: false, error: "Inquiry type required" };
      }
      if (!message || message.length < 10) {
        trackContactValidationFailure(
          "message",
          message ? "too_short" : "missing_required",
          submittedInquiryTypeForAnalytics,
        );
        toast.error("Please enter a message (at least 10 characters)");
        return { success: false, error: "Message required" };
      }

      const result = await submitContactForm({
        name,
        email,
        phone: phone || undefined,
        smsConsent,
        inquiryType: submittedInquiryType,
        message,
        honeypot: honeypot || undefined,
        attribution: getLeadAttribution("contact_form"),
      });

      if (result.success) {
        const leadProperties = {
          lead_id: result.id,
          lead_type: "contact",
          inquiry_type: submittedInquiryType,
          source: "contact_form",
        };
        analytics.formSubmitSucceeded(
          getContactFunnelProperties({
            step: "server_action",
            ...leadProperties,
          }),
        );
        analytics.contactFormSubmitted(submittedInquiryType, leadProperties);
        analytics.leadCaptured("contact", "contact_form", leadProperties);
        toast.success("Message sent. We will be in touch soon.");
        setIsSubmitted(true);
      } else {
        analytics.formSubmitFailed(
          getContactFunnelProperties({
            step: "server_action",
            inquiry_type: submittedInquiryType,
            error_type: "server_rejected",
          }),
        );
        toast.error(result.error || "Something went wrong. Please try again.");
      }

      return result;
    },
    null,
  );

  if (isSubmitted) {
    return (
      <main
        data-public-page-brand={pageBrand}
        data-roma-funnel={isRomaDarkFunnel ? "on" : undefined}
        data-roma-lead-page={isRomaDarkFunnel ? "dark" : undefined}
        className="roma-funnel-lead-page min-h-[80dvh] bg-white px-4 py-14 text-[#1f1f1f]"
      >
        <section className="mx-auto max-w-[560px] rounded-[22px] border border-[#e3e3e3] bg-white px-6 py-10 text-center shadow-[0_20px_60px_-46px_rgba(0,0,0,0.45)]">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-serif text-[26px] font-semibold leading-8">
            Message sent
          </h1>
          <p className="mt-3 text-[15px] leading-7 text-[#555]">
            Thanks for reaching out. The {restaurantCopy.shortName} team usually responds
            within 24 hours during business hours.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              variant="outline"
              className="h-12 rounded-[10px] border-[#d6d6d6] bg-white px-5 text-[15px] font-semibold text-[#1f1f1f] transition hover:bg-[#f7f7f7]"
              onClick={() => {
                setIsSubmitted(false);
                setInquiryType("");
                hasStartedContactFormRef.current = false;
              }}
            >
              Send another message
            </Button>
            {isDualRomaContact ? (
              romaLocations.map((romaLocation) => (
                <Button
                  key={romaLocation.slug}
                  asChild
                  className="h-12 rounded-[10px] bg-[var(--roma-terracotta)] px-5 text-[15px] font-semibold text-[#0d0d0d] transition hover:bg-[var(--roma-wine)]"
                >
                  <a
                    href={`tel:${romaLocation.phoneHref}`}
                    onClick={() =>
                      analytics.phoneClicked({
                        location: "contact_success",
                        cta_text: `Call ${romaLocation.eyebrow}`,
                        destination_url: `tel:${romaLocation.phoneHref}`,
                        lead_type: "contact",
                      })
                    }
                  >
                    Call {stickyCallLabel(romaLocation)}
                    <Phone className="ml-2 h-4 w-4" />
                  </a>
                </Button>
              ))
            ) : (
              <Button
                asChild
                className="h-12 rounded-[10px] bg-[var(--roma-terracotta)] px-5 text-[15px] font-semibold text-[#0d0d0d] transition hover:bg-[var(--roma-wine)]"
              >
                <a
                  href={`tel:${phoneHref}`}
                  onClick={() =>
                    analytics.phoneClicked({
                      location: "contact_success",
                      cta_text: "Call",
                      destination_url: `tel:${phoneHref}`,
                      lead_type: "contact",
                    })
                  }
                >
                  Call {phoneDisplay}
                  <Phone className="ml-2 h-4 w-4" />
                </a>
              </Button>
            )}
            <Button
              asChild
              variant="outline"
              className="h-12 rounded-[10px] border-[#d6d6d6] bg-white px-5 text-[15px] font-semibold text-[#1f1f1f] transition hover:bg-[#f7f7f7]"
            >
              <Link href="/">
                Home
                <Home className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main
      data-public-page-brand={pageBrand}
      data-roma-funnel={isRomaDarkFunnel ? "on" : undefined}
      data-roma-lead-page={isRomaDarkFunnel ? "dark" : undefined}
      className="roma-funnel-lead-page min-h-screen w-full overflow-x-hidden bg-white text-[#1f1f1f]"
    >
      <section className="mx-auto w-full max-w-[1100px] px-4 pb-8 pt-8 sm:px-6 md:pb-12 md:pt-10">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-12">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
              Contact
            </p>
            <h1 className="mt-2 font-serif text-[24px] font-semibold leading-8 text-[#151515] sm:text-[28px] sm:leading-9">
              Get in touch with {restaurantCopy.shortName}.
            </h1>
            <p className="mt-4 max-w-[460px] text-[16px] leading-[1.55] text-[#202020]">
              {contactLeadCopy.pageIntro}
            </p>

            <section
              className="mt-6 rounded-[18px] border border-[#d8d8d8] bg-[#fffaf7] p-4"
              aria-labelledby="contact-routing-heading"
            >
              <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
                Use this page when
              </p>
              <h2
                id="contact-routing-heading"
                className="mt-1 font-serif text-[20px] font-semibold leading-7 text-[#151515]"
              >
                Route the question to the right place
              </h2>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {contactRoutingLinks.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    className="rounded-[12px] border border-[#eadbd3] bg-white px-3 py-3 transition hover:bg-[var(--roma-linen)]"
                  >
                    <span className="block text-[15px] font-semibold leading-5 text-[#1f1f1f]">
                      {link.label}
                    </span>
                    <span className="mt-1 block text-[13px] leading-5 text-[#666]">
                      {link.detail}
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            <form
              id="contact-form"
              action={formAction}
              className="mt-8 max-w-[720px] space-y-7"
              onFocusCapture={() => markContactFormStarted()}
              onInvalidCapture={(event) => {
                const formData = new FormData(event.currentTarget);
                const formInquiryType = formData.get("inquiryType");
                const submittedInquiryType = isInquiryType(inquiryTypes, formInquiryType)
                  ? formInquiryType
                  : inquiryType || "unknown";
                const fieldKey = getFormControlFieldKey(event.target);

                trackContactAttempted(submittedInquiryType);
                trackContactValidationFailure(
                  fieldKey,
                  getFormControlValidityErrorType(event.target),
                  submittedInquiryType,
                );
              }}
              onPointerDownCapture={() => markContactFormStarted()}
              onSubmitCapture={(event) => {
                const formData = new FormData(event.currentTarget);
                const formInquiryType = formData.get("inquiryType");
                const submittedInquiryType = isInquiryType(inquiryTypes, formInquiryType)
                  ? formInquiryType
                  : inquiryType || "unknown";

                trackContactAttempted(submittedInquiryType);
              }}
            >
              {formState?.error ? (
                <div
                  role="alert"
                  className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-[14px] leading-6 text-red-800"
                >
                  {formState.error}
                </div>
              ) : null}

              <input
                type="text"
                name="website"
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <SaffronField label="Name" htmlFor="name">
                <Input
                  id="name"
                  name="name"
                  required
                  minLength={2}
                  maxLength={100}
                  autoComplete="name"
                  placeholder="Name"
                  disabled={isPending}
                  className={saffronInputClass}
                />
              </SaffronField>

              <SaffronField label="Email" htmlFor="email">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  spellCheck={false}
                  placeholder="Email"
                  disabled={isPending}
                  className={saffronInputClass}
                />
              </SaffronField>

              <SaffronField label="Phone (optional)" htmlFor="phone">
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="Phone"
                  disabled={isPending}
                  className={saffronInputClass}
                />
              </SaffronField>

              <div className="flex items-start gap-3 text-[12px] leading-5 text-[#565656]">
                <Checkbox
                  id="smsConsent"
                  name="smsConsent"
                  aria-describedby="smsConsent-copy"
                  disabled={isPending}
                  className="mt-[2px]"
                />
                <div>
                  <label htmlFor="smsConsent" className="block">
                    I agree to receive {contactLeadCopy.smsConsentNouns} text messages
                    from {siteConfig.name}. Message frequency varies. Message and
                    data rates may apply. Reply STOP to opt out, HELP for help.
                    Consent is not required to submit this request.
                  </label>
                  <p id="smsConsent-copy">
                    See our{" "}
                    <Link href="/privacy" className="underline underline-offset-2">
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link href="/terms" className="underline underline-offset-2">
                      Terms
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <SaffronField label="What can we help with? *" htmlFor="inquiryType">
                <Select
                  value={inquiryType || undefined}
                  onValueChange={(value) => {
                    markContactFormStarted(value as ContactInquiryType | "");
                    setInquiryType(value as ContactInquiryType | "");
                  }}
                  disabled={isPending}
                >
                  <SelectTrigger
                    id="inquiryType"
                    className={`${saffronInputClass} appearance-auto h-[50px]`}
                  >
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    {inquiryTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </SaffronField>

              <SaffronField label="Message" htmlFor="message">
                <Textarea
                  id="message"
                  name="message"
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={5}
                  placeholder="How can we help?"
                  aria-describedby="contact-message-help"
                  disabled={isPending}
                  className={saffronTextareaClass}
                />
                <p
                  id="contact-message-help"
                  className="mt-2 text-[12px] leading-5 text-[#666]"
                >
                  Minimum 10 characters.
                </p>
              </SaffronField>

              <Button
                type="submit"
                className="flex h-[50px] w-full items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-5 text-[13px] font-bold uppercase tracking-[0.02em] text-[#0d0d0d] transition-colors hover:bg-[var(--roma-wine)] focus-visible:ring-[color:var(--roma-terracotta)]/25 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={isPending}
                data-testid="contact-submit"
                data-track="contact_submit"
                data-track-location="contact_form"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Message
                  </>
                )}
              </Button>

              <p className="text-center text-[12px] leading-5 text-[#666]">
                We typically respond within 24 hours during business hours.
              </p>
            </form>
          </div>

          <aside className="min-w-0 space-y-6 lg:pt-16">
            {isDualRomaContact ? (
              romaLocations.map((romaLocation) => (
                <RomaLocationContactCard
                  key={romaLocation.slug}
                  location={romaLocation}
                  weeklyHours={weeklyHoursForRomaLocation(
                    romaLocation,
                    siteConfig,
                  )}
                  email={siteConfig.contact.email}
                />
              ))
            ) : (
              <ContactInfoCard
                siteConfig={siteConfig}
                title={contactLeadCopy.infoCardTitle}
                phoneHref={phoneHref}
                phoneDisplay={phoneDisplay}
                mapUrl={mapUrl}
                weeklyHours={weeklyHours}
              />
            )}
            <QuickLinks links={contactLeadCopy.quickLinks} />
            {siteConfig.socials.instagram ? (
              <InstagramLink siteConfig={siteConfig} restaurantCopy={restaurantCopy} />
            ) : null}
          </aside>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1100px] px-4 pb-10 sm:px-6 md:pb-12">
        <div className="border-t border-[#e5e5e5] pt-9">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            Find us
          </p>
          {isDualRomaContact ? (
            <div className="mt-6 grid gap-8 lg:grid-cols-2">
              {romaLocations.map((romaLocation) => {
                const locationMapEmbed = mapEmbedUrlForRomaLocation(romaLocation);

                return (
                  <div key={romaLocation.slug}>
                    <h2 className="font-serif text-[22px] font-semibold leading-7 text-[#151515]">
                      {romaLocation.eyebrow}
                    </h2>
                    <p className="mt-1 text-[14px] leading-6 text-[#5a5a5a]">
                      {romaLocation.addressLine1}
                      <br />
                      {romaLocation.addressLine2}
                    </p>
                    <div className="mt-4 overflow-hidden rounded-[18px] border border-[#e3e3e3] bg-white">
                      <GoogleMapEmbed
                        iframeClassName="h-[300px] sm:h-[360px]"
                        src={locationMapEmbed}
                        title={`Google Map to ${romaLocation.name}`}
                      />
                    </div>
                    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                      <Button
                        asChild
                        variant="outline"
                        className="flex h-[48px] items-center justify-center rounded-[9px] border-[#d6d6d6] bg-white px-5 text-[14px] font-bold text-[#1f1f1f] transition hover:bg-[#f7f7f7]"
                      >
                        <a
                          href={romaLocation.mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            analytics.mapClicked({
                              location: "contact_map",
                              cta_text: "Open in Google Maps",
                              destination_url: romaLocation.mapsUrl,
                            })
                          }
                        >
                          Open in Google Maps
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                      <Button
                        asChild
                        className="flex h-[48px] items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-5 text-[14px] font-bold text-[#0d0d0d] transition hover:bg-[var(--roma-wine)]"
                      >
                        <a
                          href={romaLocation.reserveUrl}
                          {...externalLinkProps(romaLocation.reserveUrl)}
                          onClick={() =>
                            analytics.reservationClicked("contact_map_reserve", {
                              location: "contact_map",
                              cta_text: "Reserve Now",
                              destination_url: romaLocation.reserveUrl,
                            })
                          }
                        >
                          Reserve Now
                          <CalendarDays className="ml-2 h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <h2 className="mt-2 font-serif text-[22px] font-semibold leading-7 text-[#151515]">
                {siteConfig.address.city}, {siteConfig.address.state}
              </h2>
              <div className="mt-6 overflow-hidden rounded-[18px] border border-[#e3e3e3] bg-white">
                <GoogleMapEmbed
                  iframeClassName="h-[300px] sm:h-[360px]"
                  src={mapEmbedSrc}
                  title={`Google Map to ${siteConfig.name}`}
                />
              </div>
              <Button
                asChild
                variant="outline"
                className="mt-4 flex h-[48px] w-full items-center justify-center rounded-[9px] border-[#d6d6d6] bg-white px-5 text-[14px] font-bold text-[#1f1f1f] transition hover:bg-[#f7f7f7] sm:w-fit"
              >
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    analytics.mapClicked({
                      location: "contact_map",
                      cta_text: "Open in Google Maps",
                      destination_url: mapUrl,
                    })
                  }
                >
                  Open in Google Maps
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </>
          )}
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1100px] px-4 pb-10 sm:px-6 md:pb-12">
        <div className="border-t border-[#e5e5e5] pt-9">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            What to expect
          </p>
          <h2 className="mt-2 font-serif text-[22px] font-semibold leading-7 text-[#151515]">
            {contactLeadCopy.trustHeading}
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {contactSeoTrustPoints.map((point) => (
              <article
                key={point.title}
                className="rounded-[16px] border border-[#e3e3e3] bg-white px-4 py-4"
              >
                <h3 className="text-[15px] font-semibold leading-6 text-[#1f1f1f]">
                  {point.title}
                </h3>
                <p className="mt-2 text-[14px] leading-6 text-[#5a5a5a]">
                  {point.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1100px] px-4 pb-32 sm:px-6 md:pb-14">
        <div className="border-t border-[#e5e5e5] pt-9">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
            FAQ
          </p>
          <h2 className="mt-2 font-serif text-[22px] font-semibold leading-7 text-[#151515]">
            Common contact questions
          </h2>
          <div className="mt-6 grid gap-x-8 gap-y-6 md:grid-cols-2">
            {contactSeoFaqs.map((faq) => (
              <article key={faq.question} className="border-t border-[#ededed] pt-4">
                <h3 className="text-[15px] font-semibold leading-6 text-[#1f1f1f]">
                  {faq.question}
                </h3>
                <p className="mt-2 text-[14px] leading-6 text-[#5a5a5a]">
                  {faq.answer}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {isDualRomaContact ? (
        <div className="fixed inset-x-0 bottom-0 z-40 space-y-2 bg-white/95 px-2 pb-2 pt-1 shadow-[0_-10px_30px_-24px_rgba(0,0,0,0.55)] backdrop-blur md:hidden">
          <div className="grid grid-cols-2 gap-2">
            {romaLocations.map((romaLocation) => (
              <a
                key={romaLocation.slug}
                href={`tel:${romaLocation.phoneHref}`}
                aria-label={`Call ${romaLocation.eyebrow}`}
                className="flex h-12 items-center justify-center rounded-[9px] border border-[#d6d6d6] bg-white px-3 text-[13px] font-bold text-[#1f1f1f]"
                onClick={() =>
                  analytics.phoneClicked({
                    location: "contact_sticky_mobile",
                    cta_text: `Call ${stickyCallLabel(romaLocation)}`,
                    destination_url: `tel:${romaLocation.phoneHref}`,
                    lead_type: "contact",
                  })
                }
              >
                Call {stickyCallLabel(romaLocation)}
              </a>
            ))}
          </div>
          <a
            href="#contact-form"
            className="flex h-12 items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-4 text-[14px] font-bold text-[#0d0d0d]"
            onClick={() => markContactFormStarted()}
          >
            Send Message
          </a>
        </div>
      ) : (
        <div className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-[0.88fr_1.12fr] gap-2 bg-white/95 px-2 pb-2 pt-1 shadow-[0_-10px_30px_-24px_rgba(0,0,0,0.55)] backdrop-blur md:hidden">
          <a
            href={`tel:${phoneHref}`}
            className="flex h-12 items-center justify-center rounded-[9px] border border-[#d6d6d6] bg-white px-4 text-[14px] font-bold text-[#1f1f1f]"
            onClick={() =>
              analytics.phoneClicked({
                location: "contact_sticky_mobile",
                cta_text: "Call",
                destination_url: `tel:${phoneHref}`,
                lead_type: "contact",
              })
            }
          >
            Call
          </a>
          <a
            href="#contact-form"
            className="flex h-12 items-center justify-center rounded-[9px] bg-[var(--roma-terracotta)] px-4 text-[14px] font-bold text-[#0d0d0d]"
            onClick={() => markContactFormStarted()}
          >
            Send Message
          </a>
        </div>
      )}
    </main>
  );
}

function SaffronField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label
        htmlFor={htmlFor}
        className="block text-[15px] font-normal leading-6 text-[#232323]"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ContactInfoCard({
  siteConfig,
  title,
  phoneHref,
  phoneDisplay,
  mapUrl,
  weeklyHours,
}: {
  siteConfig: RestaurantSiteConfig;
  title: string;
  phoneHref: string;
  phoneDisplay: string;
  mapUrl: string;
  weeklyHours: string[];
}) {
  return (
    <section className="rounded-[18px] border border-[#e3e3e3] bg-white px-4 py-5">
      <h2 className="font-serif text-[21px] font-semibold leading-7 text-[#151515]">
        {title}
      </h2>
      <div className="mt-5 space-y-5">
        <ContactInfoItem icon={MapPin} label="Address">
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.mapClicked({
                location: "contact_info",
                cta_text: "Open map",
                destination_url: mapUrl,
              })
            }
          >
            {siteConfig.address.line1}
            <br />
            {siteConfig.address.city}, {siteConfig.address.state}{" "}
            {siteConfig.address.zip}
          </a>
        </ContactInfoItem>

        <ContactInfoItem icon={Phone} label="Phone">
          <a
            href={`tel:${phoneHref}`}
            className="transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.phoneClicked({
                location: "contact_info",
                cta_text: "Call",
                destination_url: `tel:${phoneHref}`,
                lead_type: "contact",
              })
            }
          >
            {phoneDisplay}
          </a>
        </ContactInfoItem>

        <ContactInfoItem icon={Mail} label="Email">
          <a
            href={`mailto:${siteConfig.contact.email}`}
            className="break-words transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.externalExitClicked({
                location: "contact_info",
                cta_text: "Email",
                link_type: "email",
                exit_target: "email",
                exit_type: "contact",
              })
            }
          >
            {siteConfig.contact.email}
          </a>
        </ContactInfoItem>

        <ContactInfoItem icon={Clock} label="Hours">
          <span>
            {weeklyHours.map((line) => (
              <span key={line}>
                {line}
                <br />
              </span>
            ))}
          </span>
        </ContactInfoItem>
      </div>
    </section>
  );
}

function RomaLocationContactCard({
  location,
  weeklyHours,
  email,
}: {
  location: RomaLocationCopy;
  weeklyHours: string[];
  email: string;
}) {
  return (
    <section className="rounded-[18px] border border-[#e3e3e3] bg-white px-4 py-5">
      <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--roma-terracotta)]">
        {location.eyebrow}
      </p>
      <h2 className="mt-1 font-serif text-[21px] font-semibold leading-7 text-[#151515]">
        {location.name}
      </h2>
      <div className="mt-5 space-y-5">
        <ContactInfoItem icon={MapPin} label="Address">
          <a
            href={location.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.mapClicked({
                location: "contact_info",
                cta_text: "Open map",
                destination_url: location.mapsUrl,
              })
            }
          >
            {location.addressLine1}
            <br />
            {location.addressLine2}
          </a>
        </ContactInfoItem>

        <ContactInfoItem icon={Phone} label="Phone">
          <a
            href={`tel:${location.phoneHref}`}
            className="transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.phoneClicked({
                location: "contact_info",
                cta_text: `Call ${location.eyebrow}`,
                destination_url: `tel:${location.phoneHref}`,
                lead_type: "contact",
              })
            }
          >
            {location.phoneDisplay}
          </a>
        </ContactInfoItem>

        <ContactInfoItem icon={Mail} label="Email">
          <a
            href={`mailto:${email}`}
            className="break-words transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.externalExitClicked({
                location: "contact_info",
                cta_text: "Email",
                link_type: "email",
                exit_target: "email",
                exit_type: "contact",
              })
            }
          >
            {email}
          </a>
        </ContactInfoItem>

        <ContactInfoItem icon={Clock} label="Hours">
          <span>
            {weeklyHours.map((line) => (
              <span key={line}>
                {line}
                <br />
              </span>
            ))}
          </span>
        </ContactInfoItem>

        <ContactInfoItem icon={MapPin} label="Google Maps">
          <a
            href={location.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.mapClicked({
                location: "contact_info",
                cta_text: "Google Maps",
                destination_url: location.mapsUrl,
              })
            }
          >
            Open in Google Maps
          </a>
        </ContactInfoItem>

        <ContactInfoItem icon={CalendarDays} label="Reservations">
          <a
            href={location.reserveUrl}
            {...externalLinkProps(location.reserveUrl)}
            className="transition hover:text-[var(--roma-terracotta)]"
            onClick={() =>
              analytics.reservationClicked("contact_info_reserve", {
                location: "contact_info",
                cta_text: "Reserve Now",
                destination_url: location.reserveUrl,
              })
            }
          >
            Reserve Now on Google
          </a>
        </ContactInfoItem>
      </div>
    </section>
  );
}

function ContactInfoItem({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[#e3e3e3] text-[var(--roma-terracotta)]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#777]">
          {label}
        </p>
        <div className="mt-1 text-[14px] leading-6 text-[#333]">{children}</div>
      </div>
    </div>
  );
}

function QuickLinks({ links }: { links: ContactQuickLink[] }) {
  return (
    <section className="rounded-[18px] border border-[#e3e3e3] bg-white px-4 py-5">
      <h2 className="font-serif text-[21px] font-semibold leading-7 text-[#151515]">
        Quick links
      </h2>
      <div className="mt-4 divide-y divide-[#ededed]">
        {links.map((link) => {
          const Icon = link.icon;

          return (
            <Link
              key={link.href}
              href={link.href}
              className="flex min-h-[50px] items-center justify-between gap-3 py-3 text-[14px] font-semibold text-[#1f1f1f] transition hover:text-[var(--roma-terracotta)]"
              onClick={() => {
                if (link.href === "/reservations") {
                  analytics.reservationClicked("contact_quick_links", {
                    location: link.analyticsLocation,
                    cta_text: link.label,
                    destination_url: link.href,
                  });
                }
                if (link.href === "/menu") {
                  analytics.menuBrowseClicked({
                    location: link.analyticsLocation,
                    cta_text: link.label,
                    destination_url: link.href,
                  });
                }
              }}
            >
              <span className="flex min-w-0 items-center gap-3">
                <Icon className="h-4 w-4 shrink-0 text-[var(--roma-terracotta)]" />
                <span className="min-w-0 break-words">{link.label}</span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function InstagramLink({
  siteConfig,
  restaurantCopy,
}: {
  siteConfig: RestaurantSiteConfig;
  restaurantCopy: MarketingRestaurantCopy;
}) {
  return (
    <section className="rounded-[18px] border border-[#e3e3e3] bg-white px-4 py-5">
      <h2 className="font-serif text-[21px] font-semibold leading-7 text-[#151515]">
        Instagram
      </h2>
      <a
        href={siteConfig.socials.instagram}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 flex h-[48px] w-full items-center justify-center rounded-[9px] border border-[#d6d6d6] bg-white px-5 text-[14px] font-bold text-[#1f1f1f] transition hover:bg-[#f7f7f7]"
        onClick={() =>
          analytics.socialClicked("instagram", {
            location: "contact_follow_us",
            cta_text: "Instagram",
            destination_url: siteConfig.socials.instagram,
          })
        }
      >
        <InstagramGlyph className="mr-2 h-4 w-4 text-[var(--roma-terracotta)]" />
        Follow {restaurantCopy.shortName}
      </a>
    </section>
  );
}

function InstagramGlyph({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <rect
        x="3"
        y="3"
        width="18"
        height="18"
        rx="5"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle
        cx="12"
        cy="12"
        r="4"
        stroke="currentColor"
        strokeWidth="2"
      />
      <circle cx="17.5" cy="6.5" r="1.25" fill="currentColor" />
    </svg>
  );
}
